import { ColDef, ValueGetterParams } from 'ag-grid-community';
import {
  createCustomCol,
  createDateHasAllTimeCol,
  createDateRangeCol,
  createIdCol,
  createNumberCol,
  createTypoCol,
  createViewableCol,
} from '@/src/helpers/agColumnHelper';
import TableRowStatusBadge from '@/src/components/molecule/TableRowStatusBadge';
import Badge, { badgeColorData } from '@/src/components/atom/Badge';
import { NoticeCategory } from '@/src/models/NoticeDto';
import { MagazineDto } from '@/src/models/MagazineDto';
import dayjs from 'dayjs';

const getNoticeCateogoryTypeBadgeProps = (
  type: NoticeCategory,
): {
  children: string;
  badgeColor: keyof typeof badgeColorData;
} => {
  switch (type) {
    case 'common':
      return {
        children: '일반 공지',
        badgeColor: 'lightBlue',
      };
    case 'community':
      return {
        children: '커뮤니티 공지',
        badgeColor: 'lightYellow',
      };
    default:
      return {
        children: '-',
        badgeColor: 'lightGray',
      };
  }
};
const getMagazineCateogoryTypeBadgeProps = (
  type: MagazineDto['magazineType'],
): {
  children: string;
  badgeColor: keyof typeof badgeColorData;
} => {
  switch (type) {
    case 'common':
      return {
        children: '일반',
        badgeColor: 'lightYellow',
      };
    case 'advertising':
      return {
        children: '광고',
        badgeColor: 'lightPurple',
      };
    default:
      return {
        children: '-',
        badgeColor: 'lightGray',
      };
  }
};

export const noticeListColumn = (): ColDef[] => [
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '--ag-cell-widget-spacing': '0',
    },
    field: 'selectCheckbox',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    headerName: '선택',
  },
  createIdCol(),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'categoryType',
    width: 143,
    minWidth: 143,
    maxWidth: 200,
    cellRenderer: (params: ValueGetterParams) => {
      return (
        <Badge
          {...getNoticeCateogoryTypeBadgeProps(params?.data?.categoryType)}
        />
      );
    },
    headerName: '유형',
  },
  createTypoCol({
    field: 'title',
    width: 317,
    headerName: '제목',
  }),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'showedYn',
    width: 96,
    minWidth: 96,
    maxWidth: 96,
    cellRenderer: (params: ValueGetterParams) => {
      if (params.data.showedYn === true || params.data.showedYn === false) {
        return (
          <TableRowStatusBadge type="isViewable" value={params.data.showedYn} />
        );
      } else {
        return '-';
      }
    },
    headerName: '노출 여부',
  },
  createDateHasAllTimeCol({
    field: 'createdAt',
    width: 231,
    minWidth: 231,
    maxWidth: 300,
    headerName: '등록 일시',
  }),
];

export const donationListColumn = (): ColDef[] => [
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '--ag-cell-widget-spacing': '0',
    },
    field: 'selectCheckbox',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    headerName: '선택',
  },
  createIdCol(),
  createTypoCol({
    field: 'title',
    size: 'lg',
    flex: 2,
    headerName: '제목',
  }),
  createNumberCol({
    field: 'goalCredit',
    size: 'sm',
    headerName: '목표 크레딧',
  }),
  createNumberCol({
    field: 'participantsCredit',
    size: 'sm',
    headerName: '모금 크레딧',
  }),
  createDateRangeCol({
    field: 'startedAt',
    keys: ['startedAt', 'endedAt'],
    size: 'md',
    flex: 1,
    headerName: '모금 기간',
  }),
  createViewableCol({
    field: 'isExposed',
    headerName: '노출 여부',
  }),
];

export const entryListColumn = (): ColDef[] => [
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '--ag-cell-widget-spacing': '0',
    },
    field: 'selectCheckbox',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    headerName: '선택',
  },
  createIdCol(),
  createTypoCol({
    field: 'title',
    size: 'lg',
    flex: 2,
    headerName: '제목',
  }),
  createNumberCol({
    field: 'entryCredit',
    size: 'sm',
    headerName: '1회 응모 크레딧',
  }),
  createDateRangeCol({
    field: 'startedAt',
    keys: ['startedAt', 'endedAt'],
    size: 'lg',
    flex: 1,
    headerName: '응모 기간',
  }),
  createViewableCol({
    field: 'isExposed',
    headerName: '노출 여부',
  }),
];

export const magazineListColumn = (): ColDef[] => [
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '--ag-cell-widget-spacing': '0',
    },
    field: 'selectCheckbox',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    lockPosition: 'left',
    pinned: 'left',
    checkboxSelection: true,
    showDisabledCheckboxes: true,
    headerName: '선택',
  },
  createIdCol(),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'magazineType',
    width: 143,
    minWidth: 143,
    maxWidth: 200,
    cellRenderer: (params: ValueGetterParams) => {
      return (
        <Badge
          {...getMagazineCateogoryTypeBadgeProps(params?.data?.magazineType)}
        />
      );
    },
    headerName: '매거진 유형',
  },
  createTypoCol({
    field: 'title',
    size: 'lg',
    headerName: '제목',
  }),
  createViewableCol({
    field: 'isExposed',
    size: 'sm',
    headerName: '노출 여부',
  }),
  createViewableCol({
    field: 'awardedCreditYn',
    size: 'sm',
    headerName: '크레딧 지급 여부',
  }),
  createCustomCol({
    field: 'postedAt',
    width: 274,
    minWidth: 274,
    maxWidth: 300,
    cellRenderer: ({ data }: ValueGetterParams) => {
      return `${dayjs(data?.magazinePostStartedAt).format(
        'YYYY-MM-DD',
      )} ~ ${dayjs(data?.magazinePostEndedAt).format('YYYY-MM-DD')}`;
    },
    headerName: '게시 기간',
  }),
];
